import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BlockStack,
  Combobox,
  LegacyStack,
  Listbox,
  Spinner,
  Tag,
  Text,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

interface Option {
  label: string;
  value: string;
}

interface SearchableSingleValueSelectProps<Row> {
  value: string;
  onChange: (value: string) => void;
  isRequired?: boolean;
  disabled?: boolean;
  labelKey: string;
  requiredErrorKey: string;
  fetchOptions: (companyId: string) => Promise<Row[]>;
  mapOption: (row: Row) => Option;
}

export default function SearchableSingleValueSelect<Row>({
  value,
  onChange,
  isRequired = false,
  disabled = false,
  labelKey,
  requiredErrorKey,
  fetchOptions,
  mapOption,
}: SearchableSingleValueSelectProps<Row>) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const [options, setOptions] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(() => Boolean(companyId));

  const selectedLabel = useMemo(
    () => options.find((item) => item.value === value)?.label || "",
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    if (!inputValue) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [options, inputValue]);

  useEffect(() => {
    async function loadOptions() {
      if (!companyId) {
        setOptions([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const data = await fetchOptions(companyId);
        const mapped = (data || []).map(mapOption);
        setOptions(mapped);
      } finally {
        setIsLoading(false);
      }
    }
    loadOptions();
  }, [companyId, fetchOptions, mapOption]);

  const handleSelect = useCallback(
    (selected: string) => {
      onChange(selected);
      setInputValue("");
    },
    [onChange]
  );

  const handleRemoveSelected = useCallback(() => {
    onChange("");
    setInputValue("");
  }, [onChange]);

  if (isLoading) {
    return (
      <BlockStack gap="200">
        <Text variant="bodySm" as="span" tone="subdued">
          {t(labelKey)}
        </Text>
        <Spinner size="small" accessibilityLabel={t(labelKey)} />
      </BlockStack>
    );
  }

  return (
    <Combobox
      activator={
        <Combobox.TextField
          label={t(labelKey)}
          autoComplete="off"
          value={inputValue}
          placeholder={t("search_tags")}
          onChange={setInputValue}
          requiredIndicator={isRequired}
          disabled={disabled}
          error={isRequired && !value ? t(requiredErrorKey) : ""}
          verticalContent={
            selectedLabel ? (
              <LegacyStack spacing="extraTight">
                <Tag onRemove={disabled ? undefined : handleRemoveSelected}>
                  {selectedLabel}
                </Tag>
              </LegacyStack>
            ) : null
          }
        />
      }
    >
      <Listbox onSelect={handleSelect}>
        {filteredOptions.map((option) => (
          <Listbox.Option
            key={option.value}
            value={option.value}
            selected={option.value === value}
            accessibilityLabel={option.label}
          >
            <Listbox.TextOption selected={option.value === value}>
              {option.label}
            </Listbox.TextOption>
          </Listbox.Option>
        ))}
      </Listbox>
    </Combobox>
  );
}
